import { useCubeQuery } from '@cubejs-client/react';
import { FormatAlignJustify, ContactMailOutlined } from '@mui/icons-material';
import { Button, Dialog, ListItemIcon, Typography } from '@mui/material';
import { ContactRoleVariant, Language } from '@procureai/constellation/foundation/pai-object/curated-zone/contact';
import {
  ExtendedList,
  ExtendedListItem,
  ExtendedListItemText,
  Widget,
  WidgetContent,
  WidgetFooter,
  WidgetHeader,
  WidgetIcon,
  useScrollIntoView,
} from '@procureai/ui-mui';
import * as R from 'ramda';
import { isEmpty } from 'ramda';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { I18nNamespace } from '~/common/types';
import { DimContactField } from '~/common/types/cubes/DimContact';
import { CircularProgressCentered } from '~/components/CircularProgressCentered/CircularProgressCentered';
import { useModuleContext } from '~/components/ModuleContextProvider';
import { PlaceholderWidget } from '~/components/PlaceholderWidget/PlaceholderWidget';
import { useOpenClose } from '~/hooks';
const MAX_ITEMS = 4;

type SupplierContact = {
  id: string;
  title: string;
  firstName: string;
  lastName: string;
  email: string;
  role: ContactRoleVariant;
  preferredLanguage: Language;
};

export const SupplierContactListWidget = () => {
  const { entityId } = useModuleContext();
  const { t } = useTranslation(I18nNamespace.widgets, { keyPrefix: 'SupplierContactListWidget' });
  const { t: tLanguage } = useTranslation(undefined, { keyPrefix: 'language' });
  const [widgetRef, active] = useScrollIntoView({ hash: 'supplier-contact-list' });

  const [dialogProps, dialogOpen] = useOpenClose();

  const { isLoading, resultSet } = useCubeQuery({
    dimensions: [
      DimContactField.ID,
      DimContactField.TITLE,
      DimContactField.FIRST_NAME,
      DimContactField.LAST_NAME,
      DimContactField.EMAIL,
      DimContactField.ROLE,
      DimContactField.PREFERRED_LANGUAGE,
    ],
    filters: [
      {
        member: DimContactField.SUPPLIER_ID,
        operator: 'equals',
        values: [entityId],
      },
    ],
    order: [
      [DimContactField.FIRST_NAME, 'asc'],
      [DimContactField.LAST_NAME, 'asc'],
    ],
  });
  const results =
    resultSet?.rawData().map(
      (p): SupplierContact => ({
        id: p[DimContactField.ID] as string,
        title: p[DimContactField.TITLE] as string,
        firstName: p[DimContactField.FIRST_NAME] as string,
        lastName: p[DimContactField.LAST_NAME] as string,
        email: p[DimContactField.EMAIL] as string,
        role: p[DimContactField.ROLE] as ContactRoleVariant,
        preferredLanguage: p[DimContactField.PREFERRED_LANGUAGE] as Language,
      }),
    ) ?? [];

  const totalCount = results.length;
  const contacts = R.sortWith([R.descend((obj) => obj.role === ContactRoleVariant.PRIMARY)], results);
  const contactsSlice = contacts.slice(0, MAX_ITEMS);
  const hasMore = totalCount > MAX_ITEMS;

  const renderItem = useMemo(
    () =>
      ({ item }: { item: SupplierContact }) => {
        const { id, title, firstName, lastName, email, role, preferredLanguage } = item;
        const color = role === ContactRoleVariant.PRIMARY ? 'success' : 'info';
        return (
          <ExtendedListItem key={id} color={color} component="div">
            <ListItemIcon>
              <ContactMailOutlined />
            </ListItemIcon>
            <ExtendedListItemText
              primary={[title, firstName, lastName].join(' ')}
              primaryLines={1}
              secondaryLines={2}
              caption={
                <>
                  {email && (
                    <Typography variant="body2" color="textSecondary">
                      {email}
                    </Typography>
                  )}
                  {role && role !== ContactRoleVariant.UNKNOWN && (
                    <Typography variant="body2" color="textSecondary">
                      {t(`role.${role}`)}
                    </Typography>
                  )}
                  {preferredLanguage && (
                    <Typography variant="body2" color="textSecondary">
                      {t(`preferredLanguage`, { language: tLanguage(preferredLanguage) })}
                    </Typography>
                  )}
                </>
              }
            />
          </ExtendedListItem>
        );
      },
    [t, tLanguage],
  );

  return (
    <>
      <Widget ref={widgetRef} active={active} data-intercom-target="SupplierContactList">
        <WidgetHeader title={t('title')} icon={<WidgetIcon icon={<FormatAlignJustify fontSize="small" />} />} />
        <WidgetContent>
          {!R.isEmpty(contacts) ? (
            <ExtendedList items={contactsSlice} renderItem={renderItem} />
          ) : isLoading ? (
            <CircularProgressCentered sx={{ position: 'relative' }} />
          ) : (
            <PlaceholderWidget />
          )}
        </WidgetContent>
        <WidgetFooter>
          <Button variant="text" disabled={!hasMore || isEmpty(contacts)} onClick={dialogOpen}>
            {t('showAll', { count: totalCount || 0 })}
          </Button>
        </WidgetFooter>
      </Widget>
      <Dialog {...dialogProps}>
        <WidgetHeader title={t('title')} icon={<WidgetIcon icon={<FormatAlignJustify fontSize="small" />} />} />
        <ExtendedList items={contacts} renderItem={renderItem} />
      </Dialog>
    </>
  );
};
